<template>
    <div
        :class="{ filled: filled, disabled: disabled }"
        class="tl-input-file-upload"
        data-testid="input-file-upload"
    >
        <div
            v-if="title"
            class="tl-title"
        >{{ title }}</div>

        <div class="tl-input-wrapper">
            <div class="tl-label">
                <div :class="(filesError || error) && 'error'">{{ label }}</div>
            </div>

            <div
                v-if="loading"
                class="main-loader"
            >
                <LoadingDots />
            </div>

            <div
                v-if="disabled"
                class="lock"
            />

            <input
                ref="fileInputRef"
                class="file-input"
                type="file"
                :accept="extensions.split(',').map(ext => `.${ext}`).join(',')"
                multiple
                @click="fileInputRef!.value = ''"
                @change="handleFileSelect"
            />
            <div
                class="drop-zone no-user-select"
                @dragenter.prevent
                @dragover.prevent
                @drop="handleDrop"
                @click="handleDropZoneClick"
            >
                <i class="icon mi-cloud-upload" />
                <p>{{ t('input-file-upload.drop-zone', { maxFileSize: maxFileSizeMB, extensions: extensions.toUpperCase().split(',').join(', ') }) }}</p>
            </div>

            <div
                v-if="Object.values(modelState).some(f => f.progress > 0) || props.progress > 0"
                class="summary"
            >{{ summary }}</div>

            <transition-group
                name="list-fade"
                tag="ul"
                class="file-list"
                data-testid="input-file-upload-file-list"
            >
                <li
                    v-for="(file, index) in modelState"
                    :key="index"
                    class="file-row"
                >
                    <span class="file">
                        <transition-group
                            name="fade"
                            tag="span"
                            class="icons"
                        >
                            <LoadingCircle
                                v-if="file.loading"
                                key="circle"
                            />
                            <i
                                v-if="file.error?.length"
                                key="error"
                                class="mi-warning status error"
                            />
                            <i
                                v-if="!file.error?.length && file.verified"
                                key="verified"
                                class="mi-check status success"
                            />
                        </transition-group>

                        <span
                            v-if="file.resource"
                            class="resource-name"
                            data-testid="file-name"
                            @click="emit('download-document', { ...file.resource, componentId: id })"
                        >{{ file.name }}</span>

                        <span
                            v-else
                            class="file-name"
                            data-testid="file-name"
                        >{{ file.name }}</span>

                        <span
                            class="pull-right"
                            data-testid="file-delete"
                        >
                            <i
                                class="mi-close"
                                @click.stop="removeFile(file)"
                            />
                        </span>
                    </span>

                    <span
                        v-show="0 < file.progress && file.progress < 1"
                        class="progress"
                        :style="{ width: file.progress * 100 + '%' }"
                    />

                    <ErrorText
                        v-if="file.error?.length"
                        :error="file.error"
                    />
                </li>
            </transition-group>
            <span
                v-show="0 < progress && progress < 1"
                class="progress"
                :style="{ width: progress * 100 + '%' }"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted } from 'vue'
import useTools from 'widget-components/utilities/tools'
import { useI18n } from 'widget-components/utilities/i18n'
import ErrorText from 'widget-components/components/ErrorText/ErrorText.vue'
import LoadingDots from 'widget-components/components/LoadingDots'
import LoadingCircle from 'widget-components/components/LoadingCircle'
import type { InputDocumentItem } from 'widget-components/components/InputFileUpload/types'

const { toBase64 } = useTools()
const { t } = useI18n()

const props = withDefaults(defineProps<{
    id?: string
    modelValue: Record<string, InputDocumentItem>
    initialValue?: Record<string, InputDocumentItem>
    loading?: boolean
    disabled?: boolean
    maxFileSize: number
    extensions: string
    progress?: number
    error?: string
    label?: string
    title?: string
    uploadLimit?: string | number
}>(), {
    modelValue: () => ({}),
    maxFileSize: 10 * 1024 * 1024, // 10MB
    extensions: 'jpg,jpeg,png',
    progress: 0,
    error: '',
})

const emit = defineEmits([
    'update:modelValue',
    'error',
    'file:add',
    'file:remove',
    'download-document',
])

const filesError = computed(() => {
    if (props.uploadLimit) {
        const maxFilesCount = Number(props.uploadLimit)
        if (Object.values(modelState.value)?.length > maxFilesCount) {
            return t('input-file-upload.error.upload-limit', { maxFilesCount })
        }
    }

    const filesError = Object.values(modelState.value).find(file => file.error?.length)?.error

    return filesError || ''
})

onMounted(() => {
    watch(filesError, () => {
        emit('error', filesError?.value || '')
    }, { immediate: true })
})

const summary = computed(() => {
    const filesCount = Object.keys(modelState.value)?.length

    if (!filesCount) return ''

    const { filesUploaded, isUploadRunning } = Object.values(modelState.value).reduce((res, file) => {
        if (
            0 < file.progress && file.progress < 1 ||
            0 < props.progress && props.progress < 1
        ) {
            res.isUploadRunning = true
        }
        if (file.uploaded || file.progress > 0 || props.progress > 0) {
            res.filesUploaded ++
        }
        return res
    }, { filesUploaded: 0, isUploadRunning: false })

    const files = `${filesUploaded}/${filesCount}`

    return isUploadRunning
        ? t('input-file-upload.status.uploading', { files })
        : t('input-file-upload.status.uploaded', { files })
})

const fileInputRef = ref<HTMLInputElement>()

const modelState = ref(props.modelValue)
const modelValue = computed(() => props.modelValue)

const filled = computed(() => !!modelState.value)
const label = computed(() => props.label)

const maxFileSizeMB = computed(() => (props.maxFileSize / 1024 / 1024).toFixed(2))

const loadFile = async (file: File) => {
    const item = modelState.value[file.name]

    const data = item.error?.length
        ? ''
        : await toBase64(file)

    modelState.value = {
        ...modelState.value,
        [file.name]: { ...item, data },
    }

    emit('file:add', modelState.value[file.name])
}

const validateFile = (file: InputDocumentItem) => {
    const errors = []
    const extension = file.name.split('.').pop()!

    if (!props.extensions.includes(extension)) {
        errors.push(
            t('input-file-upload.error.type', { types: props.extensions?.split(',').join(', ') }),
        )
    }

    if ((file.size ?? 0) > props.maxFileSize) {
        errors.push(t('input-file-upload.error.size', { size: `${ props.maxFileSize / 1024 / 1024 }MB` }))
    }

    if (Object.values(modelState.value).length > Number(props.uploadLimit)) {
        const maxFilesCount = Number(props.uploadLimit)
        errors.push(t('input-file-upload.error.upload-limit', { maxFilesCount }))
    }

    file.error = errors.join(', ')
}

const addFile = async (file: File) => {
    const item = {
        name: file.name,
        size: file.size,
        data: '',
        error: '',
    } as InputDocumentItem

    validateFile(item)

    modelState.value[file.name] = item

    await loadFile(file)
}

const removeFile = (file: InputDocumentItem) => {
    delete modelState.value[file.name]
    emit('file:remove', file)
}

const handleDrop = async (event: DragEvent) => {
    event.preventDefault()
    Array
        .from(event.dataTransfer!.files || [])
        .forEach((file) => addFile(file))
}

const handleDropZoneClick = () => {
    fileInputRef.value!.value = ''
    fileInputRef.value!.click()
}

const handleFileSelect = async (event: Event) => {
    Array
        .from((event.target as HTMLInputElement)?.files || [])
        .forEach(async (file) => await addFile(file))
}

onMounted(() => {
    if (props.initialValue) {
        modelState.value = props.initialValue
    }
})

watch(modelState, (val) => {
    emit('update:modelValue', val)
}, { deep: true })

watch(modelValue, (val) => {
    if (val !== modelState.value) modelState.value = val
}, { deep: true })

</script>

<style scoped lang="sass">
@import './InputFileUpload.sass'
</style>
